import { MedplumClient, parseReference } from "@medplum/core";
import { BundleEntry, Consent, Patient, Questionnaire, Reference } from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { FormsLibrarySection } from "@/fhir/enums/quilted-health";
import {
  getExtensionValues,
  getOrganizationReference,
  getResourcesFromBundle,
  joinReferenceString,
} from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

import {
  createConsentResourceBundleEntry,
  createProvenanceResourceBundleEntry,
  createQuestionnaireResponseResourceBundleEntry,
  createSupersededConsentBundleEntries,
  getPreviousConsentsByQuestionnaireId,
} from "../utils/consents";
import { ConsentFormType } from "../zod-schemas/consents";

/** Enough headroom for a client's full consent history; the default page size is 20. */
export const CONSENT_HISTORY_SEARCH_COUNT = 200;

type CreateSignatureAndConsentResourceProps = {
  medplum: MedplumClient;
  formValues: ConsentFormType;
  patientReference: Reference<Patient>;
  questionnaireIds: string[];
  practiceId: string;
  previousConsentsByQuestionnaireId?: Record<string, Consent[]>;
};

export type CreateSignatureAndConsentResult =
  | { ok: true; data: { consentCount: number; supersededConsentCount: number } }
  | { ok: false; reason: string; details?: unknown };

/**
 * Lists the active consent forms configured for a practice.
 *
 * @param medplum - Authenticated Medplum client
 * @param organizationReference - The practice reference (`Organization/{id}`) that scopes the search
 * @returns The practice's active consent Questionnaires, with the search bundle
 */
export const fetchPracticeConsentQuestionnaires = async (
  medplum: MedplumClient,
  organizationReference: string,
) =>
  medplum.searchResources("Questionnaire", {
    context: FormsLibrarySection.ConsentForms,
    status: "active",
    _compartment: organizationReference,
  });

export const fetchIntakeConsentsQuestionnaire = async (
  medplum: MedplumClient,
  patientId: string,
  practiceId: string,
) => {
  const questionnairesResults = await fetchPracticeConsentQuestionnaires(
    medplum,
    getOrganizationReference(practiceId),
  );

  const intakeQuestionnaire = await fetchIntakeQuestionnaire(medplum, practiceId);
  if (!intakeQuestionnaire)
    return { consentQuestionnaires: [], previousConsentsByQuestionnaireId: {} };

  const intakeRelatedConsentsQuestionnaireIds =
    (
      getExtensionValues(
        intakeQuestionnaire,
        Extensions.QuestionnaireRelatedConsent,
      ) as Reference<Questionnaire>[]
    )?.map((consent) => parseReference(consent)[1]) ?? [];

  const questionnaires = getResourcesFromBundle<Questionnaire>(
    "Questionnaire",
    questionnairesResults.bundle,
  );

  const consentQuestionnaires = questionnaires.filter((questionnaire) =>
    intakeRelatedConsentsQuestionnaireIds.includes(questionnaire.id!),
  );

  let previousConsentsByQuestionnaireId: Record<string, Consent[]> = {};

  if (consentQuestionnaires.length) {
    previousConsentsByQuestionnaireId = await fetchPreviousConsentsByQuestionnaireId(
      medplum,
      consentQuestionnaires.map((questionnaire) => questionnaire.id!),
      patientId,
    );
  }

  return { consentQuestionnaires, previousConsentsByQuestionnaireId };
};

export const fetchIntakeQuestionnaire = async (medplum: MedplumClient, practiceId: string) => {
  const questionnaire = await medplum.searchResources("Questionnaire", {
    url: Questionnaires.ClientIntake,
    status: "active",
    _compartment: getOrganizationReference(practiceId),
  });

  return questionnaire[0];
};

/**
 * Collects every consent a client has already signed for the given questionnaires, grouped by
 * questionnaire.
 *
 * Reads to the end of the search rather than a single page: the transaction has to retire each
 * consent it replaces, and a truncated page would silently leave an older consent reading as
 * current alongside the new one.
 */
export const fetchPreviousConsentsByQuestionnaireId = async (
  medplum: MedplumClient,
  questionnaireIds: string[],
  patientId: string,
): Promise<Record<string, Consent[]>> => {
  if (questionnaireIds.length === 0)
    throw new Error("Questionnaire not found for consent Questionnaire Response");

  const entry: BundleEntry[] = [];

  for await (const page of medplum.searchResourcePages("QuestionnaireResponse", {
    _revinclude: "Consent:source-reference",
    subject: joinReferenceString("Patient", patientId),
    questionnaire: questionnaireIds
      .map((questionnaireId) => joinReferenceString("Questionnaire", questionnaireId))
      .join(","),
    _sort: "-authored",
    _count: CONSENT_HISTORY_SEARCH_COUNT,
  })) {
    entry.push(...(page.bundle.entry ?? []));
  }

  return getPreviousConsentsByQuestionnaireId({
    resourceType: "Bundle",
    type: "searchset",
    entry,
  });
};

/**
 * Writes one consent submission as its own records.
 *
 * Every submission creates a new `QuestionnaireResponse` and `Consent` so a previously signed
 * consent stays intact with its own answers, signature and timestamp. Consents the submission
 * replaces are retired to `inactive` in the same transaction, leaving exactly one current record
 * per form.
 */
export const createSignatureAndConsentResources = async ({
  medplum,
  formValues,
  patientReference,
  questionnaireIds,
  practiceId,
  previousConsentsByQuestionnaireId,
}: CreateSignatureAndConsentResourceProps): Promise<CreateSignatureAndConsentResult> => {
  if (!questionnaireIds?.length) {
    return { ok: false, reason: "No consent questionnaires were provided." };
  }

  const questionnaireResponseFullUrls = questionnaireIds.map(() => `urn:uuid:${uuidv4()}`);
  const consentFullUrls = questionnaireIds.map(() => `urn:uuid:${uuidv4()}`);

  const questionnaireResponseBundleEntry = createQuestionnaireResponseResourceBundleEntry(
    questionnaireIds,
    patientReference,
    questionnaireResponseFullUrls,
    formValues,
    practiceId,
  );

  const consentBundleEntry = createConsentResourceBundleEntry(
    patientReference,
    questionnaireResponseFullUrls,
    formValues,
    practiceId,
    consentFullUrls,
  );

  // Keyed by the response that replaces them so each form's Provenance targets only its own
  // retired consents. A consent reaches exactly one questionnaire through its response, so the
  // flattened entries below carry no duplicate ids into the transaction.
  const supersededConsentsByResponseFullUrl = questionnaireIds.reduce<
    Record<string, BundleEntry<Consent>[]>
  >((supersededByResponse, questionnaireId, index) => {
    const supersededEntries = createSupersededConsentBundleEntries(
      previousConsentsByQuestionnaireId?.[questionnaireId] ?? [],
    );

    if (supersededEntries.length) {
      supersededByResponse[questionnaireResponseFullUrls[index]] = supersededEntries;
    }

    return supersededByResponse;
  }, {});

  const supersededConsentBundleEntry = Object.values(supersededConsentsByResponseFullUrl).flat();

  const provenanceBundleEntry = createProvenanceResourceBundleEntry(
    patientReference,
    practiceId,
    questionnaireResponseBundleEntry,
    consentBundleEntry,
    supersededConsentsByResponseFullUrl,
  );

  const entry = [
    ...questionnaireResponseBundleEntry,
    ...consentBundleEntry,
    ...supersededConsentBundleEntry,
    ...provenanceBundleEntry,
  ];

  try {
    await medplum.executeBatch({
      resourceType: "Bundle",
      type: "transaction",
      entry,
    });

    medplum.invalidateSearches("QuestionnaireResponse");
    medplum.invalidateSearches("Consent");

    return {
      ok: true,
      data: {
        consentCount: consentBundleEntry.length,
        supersededConsentCount: supersededConsentBundleEntry.length,
      },
    };
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  }
};
